import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Rating, Chip, Skeleton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import g2TravelApi from '../../services/g2TravelApi';
import { useCurrency } from '../../context/CurrencyContext';
import './HotelCardHorizontal.css';

const HotelCardHorizontal = ({
  hotel,
  detailTo,
  linkState = null,
  searchParams = {},
  prefetchedPrice = null,
  prefetchedCurrency = 'GBP',
}) => {
  const { formatFrom } = useCurrency();
  const hasPrefetchedPrice = Number.isFinite(Number(prefetchedPrice)) && Number(prefetchedPrice) > 0;
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(!hasPrefetchedPrice);
  useEffect(() => {
    if (hasPrefetchedPrice) {
      setPriceData({ minPrice: Number(prefetchedPrice), currency: prefetchedCurrency || 'GBP' });
      setPriceLoading(false);
      return;
    }

    const fetchPrice = async () => {
      try {
        setPriceLoading(true);
        const hotelId = hotel.id || hotel.code;
        if (hotelId) {
          const checkInDate = searchParams.checkIn || null;
          const nights = searchParams.nights || 1;
          const adults = searchParams.adults || 1;
          const children = searchParams.children || 0;
          const nationality = searchParams.nationality || 'GB';
          const currency = searchParams.currency || 'GBP';
          const price = await g2TravelApi.getMinimumPrice(
            hotelId,
            checkInDate,
            nights,
            adults,
            children,
            nationality,
            currency
          );
          setPriceData(price);
        }
      } catch (error) {
        console.error('Error fetching price:', error);
        setPriceData(null);
      } finally {
        setPriceLoading(false);
      }
    };

    if (hotel.id || hotel.code) {
      fetchPrice();
    }
  }, [
    hasPrefetchedPrice,
    prefetchedPrice,
    prefetchedCurrency,
    hotel.id,
    hotel.code,
    hotel.name,
    searchParams.checkIn,
    searchParams.nights,
    searchParams.adults,
    searchParams.children,
    searchParams.nationality,
    searchParams.currency,
  ]);

  const getStarRating = (ratingString) => {
    if (!ratingString) return 0;
    const match = ratingString.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getReviewScore = () => {
    if (hotel.rating) {
      const match = hotel.rating.match(/(\d+\.?\d*)/);
      if (match) return parseFloat(match[1]);
    }
    return (Math.random() * 2.5 + 7.0).toFixed(1);
  };

  const getReviewText = (score) => {
    if (score >= 9.0) return 'Wonderful';
    if (score >= 8.5) return 'Excellent';
    if (score >= 8.0) return 'Very Good';
    if (score >= 7.0) return 'Good';
    return 'Pleasant';
  };

  const starRating = getStarRating(hotel.rating);
  const reviewScore = parseFloat(getReviewScore());
  const reviewText = getReviewText(reviewScore);
  const reviewCount = Math.floor(Math.random() * 5000) + 100;

  const price = hasPrefetchedPrice
    ? Number(prefetchedPrice)
    : (priceData?.minPrice || priceData?.minSellingPrice || null);
  const currency = priceData?.currency || 'GBP';

  const label = `See availability for ${hotel.name || 'hotel'}`;

  return (
    <Box
      component={RouterLink}
      to={detailTo}
      state={linkState}
      className="hotel-card-horizontal"
      aria-label={label}
    >
      <Box className="hotel-card-horizontal__content">
        <Box className="hotel-card-horizontal__header">
          <Box className="hotel-card-horizontal__info">
            <Box className="hotel-card-horizontal__title-row">
              <Box className="hotel-card-horizontal__badges">
                {hotel.countryCode && (
                  <Chip
                    label={hotel.countryCode}
                    size="small"
                    className="hotel-card-horizontal__country-badge hotel-card-horizontal__country-badge--inline"
                  />
                )}
                {starRating > 0 && (
                  <Chip
                    icon={<StarIcon className="hotel-card-horizontal__star-icon" />}
                    label={`${starRating} Star`}
                    size="small"
                    className="hotel-card-horizontal__star-badge hotel-card-horizontal__star-badge--inline"
                  />
                )}
              </Box>
            </Box>
            <Typography
              component="h3"
              className="hotel-card-horizontal__name"
              sx={{ fontWeight: '800 !important' }}
            >
              {hotel.name || 'Hotel'}
            </Typography>
            <Box className="hotel-card-horizontal__location">
              <LocationOnIcon className="hotel-card-horizontal__location-icon" />
              <Typography className="hotel-card-horizontal__location-text">
                {hotel.address1 || hotel.cityName || hotel.location || 'Location not available'}
                {hotel.cityName && hotel.cityName !== hotel.address1 && `, ${hotel.cityName}`}
                {hotel.countryName && `, ${hotel.countryName}`}
              </Typography>
              <Typography className="hotel-card-horizontal__distance">
                {Math.floor(Math.random() * 5 + 0.5)} miles from center
              </Typography>
            </Box>
          </Box>
          <Box className="hotel-card-horizontal__rating">
            <Box className="hotel-card-horizontal__rating-badge">
              <Typography className="hotel-card-horizontal__rating-score">{reviewScore}</Typography>
              <Typography className="hotel-card-horizontal__rating-text">{reviewText}</Typography>
            </Box>
            <Typography className="hotel-card-horizontal__review-count">
              {reviewCount.toLocaleString()} reviews
            </Typography>
          </Box>
        </Box>

        <Box className="hotel-card-horizontal__details">
          <Box className="hotel-card-horizontal__room-info">
            <Typography className="hotel-card-horizontal__room-type">
              {starRating > 0 && (
                <Rating
                  value={starRating}
                  readOnly
                  size="small"
                  className="hotel-card-horizontal__stars"
                />
              )}
              <span>Double room</span>
            </Typography>
            <Typography className="hotel-card-horizontal__room-features">
              Free WiFi • Air conditioning • Private bathroom
            </Typography>
          </Box>
          <Box className="hotel-card-horizontal__price-section">
            {priceLoading ? (
              <Skeleton variant="text" width={100} height={40} />
            ) : price ? (
              <>
                <Typography className="hotel-card-horizontal__price">
                  {formatFrom(Math.round(price), currency)}
                </Typography>
                <Typography className="hotel-card-horizontal__price-note">
                  Includes taxes and fees
                </Typography>
              </>
            ) : (
              <>
                <Typography className="hotel-card-horizontal__price">Check availability</Typography>
                <Typography className="hotel-card-horizontal__price-note">Price on request</Typography>
              </>
            )}
            <Box
              component="span"
              className="hotel-card-horizontal__button hotel-card-horizontal__button--static"
            >
              See availability
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HotelCardHorizontal;
